import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sun, Moon, Sunrise, Sunset, Eye, EyeOff, Keyboard, Monitor, Grid, Compass, Type, ArrowUpDown, ZoomIn, Settings, Gamepad2, ChevronDown, Mouse, Move, Search, MousePointerClick } from 'lucide-react';
import { useTheme } from '../styles/ThemeContext';
import { useSelections } from './SelectionsContext';
interface SettingsMenuProps {
  isOpen: boolean;
  onClose: () => void;
  initialTab?: 'settings' | 'controls';
  onTabChange?: (tab: 'settings' | 'controls') => void;
}
type TabId = 'settings' | 'controls';
export function SettingsMenu({
  isOpen,
  onClose,
  initialTab = 'settings',
  onTabChange
}: SettingsMenuProps) {
  const {
    isDark,
    toggleTheme
  } = useTheme();
  const {
    allowOverrides,
    setAllowOverrides
  } = useSelections();
  const [activeTab, setActiveTab] = useState<TabId>(initialTab);
  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
    }
  }, [isOpen, initialTab]);
  const [daytime, setDaytime] = useState<'morning' | 'day' | 'evening'>('day');
  const [showFurniture, setShowFurniture] = useState(true);
  const [showDoors, setShowDoors] = useState(true);
  const [showCompass, setShowCompass] = useState(true);
  const [showLabels, setShowLabels] = useState(true);
  const [graphicsQuality, setGraphicsQuality] = useState(100);
  const [moveSpeed, setMoveSpeed] = useState(50);
  const [lookSpeed, setLookSpeed] = useState(50);
  const [invertY, setInvertY] = useState(false);
  const [teleportToOption, setTeleportToOption] = useState(true);
  const [cameraHeight, setCameraHeight] = useState(170);
  const [zoomLevel, setZoomLevel] = useState(100);
  const handleTabChange = (tab: TabId) => {
    setActiveTab(tab);
    onTabChange?.(tab);
  };
  const daytimeOptions = [{
    id: 'morning',
    label: 'Morning',
    icon: Sunrise
  }, {
    id: 'day',
    label: 'Day',
    icon: Sun
  }, {
    id: 'evening',
    label: 'Evening',
    icon: Sunset
  }];
  const tabs: {
    id: TabId;
    label: string;
    icon: React.ElementType;
  }[] = [{
    id: 'settings',
    label: 'Settings',
    icon: Settings
  }, {
    id: 'controls',
    label: 'Controls',
    icon: Gamepad2
  }];
  const sliderClass = 'w-full h-1 bg-slate-200 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-current text-teal-500 dark:text-cyan-500 dark:bg-slate-700';
  return <AnimatePresence data-id="element-2079">
      {isOpen && <motion.div initial={{
      opacity: 0,
      x: 20,
      scale: 0.95
    }} animate={{
      opacity: 1,
      x: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      x: 20,
      scale: 0.95
    }} transition={{
      duration: 0.2,
      ease: 'easeOut'
    }} className={`fixed top-6 right-24 z-40 w-80 rounded-2xl shadow-2xl backdrop-blur-xl border overflow-hidden ${isDark ? 'bg-slate-900/90 border-white/10' : 'bg-white/90 border-white/40'}`} data-id="element-2080">
          {/* Header with Tabs */}
          <div className={`px-2 pt-3 pb-0 ${isDark ? 'bg-white/5' : 'bg-gray-50/50'}`} data-id="element-2081">
            <div className="flex items-center justify-between px-3 mb-3" data-id="element-2082">
              <div className="flex items-center gap-3" data-id="element-2083">
                {tabs.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return <button key={tab.id} onClick={() => handleTabChange(tab.id)} className={`relative flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors ${isActive ? isDark ? 'text-white bg-white/10' : 'text-gray-900 bg-black/5' : isDark ? 'text-slate-500 hover:text-white' : 'text-gray-400 hover:text-gray-900'}`} data-id="element-2084">
                      <Icon className="w-3.5 h-3.5" data-id="element-2085" />
                      {tab.label}
                    </button>;
            })}
              </div>
              <button onClick={onClose} className={`p-1 rounded-lg transition-colors ${isDark ? 'text-white/50 hover:text-white hover:bg-white/10' : 'text-gray-400 hover:text-gray-900 hover:bg-gray-100'}`} data-id="element-2086">
                <X className="w-4 h-4" data-id="element-2087" />
              </button>
            </div>
            <div className={`h-px ${isDark ? 'bg-white/10' : 'bg-gray-200'}`} data-id="element-2088" />
          </div>

          {/* Tab Content */}
          <div className="p-5 space-y-6 max-h-[calc(100vh-160px)] overflow-y-auto custom-scrollbar" data-id="element-2089">
            <AnimatePresence mode="wait" data-id="element-2090">
              {activeTab === 'settings' ? <motion.div key="settings" initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: 10
          }} transition={{
            duration: 0.15
          }} className="space-y-6" data-id="element-2091">
                  {/* Time of Day */}
                  <section data-id="element-2092">
                    <h4 className={`text-xs font-bold uppercase tracking-wider mb-3 ${isDark ? 'text-slate-400' : 'text-gray-500'}`} data-id="element-2093">
                      Time of Day
                    </h4>
                    <div className="grid grid-cols-3 gap-2" data-id="element-2094">
                      {daytimeOptions.map(option => {
                  const Icon = option.icon;
                  const isActive = daytime === option.id;
                  return <button key={option.id} onClick={() => setDaytime(option.id as any)} className={`flex flex-col items-center gap-2 p-2 rounded-xl transition-all ${isActive ? isDark ? 'bg-cyan-500/20 text-cyan-400 ring-1 ring-cyan-500/50' : 'bg-teal-50 text-teal-600 ring-1 ring-teal-200' : isDark ? 'bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white' : 'bg-gray-50 text-gray-500 hover:bg-gray-100 hover:text-gray-900'}`} data-id="element-2095">
                            <Icon className="w-5 h-5" data-id="element-2096" />
                            <span className="text-[10px] font-medium" data-id="element-2097">
                              {option.label}
                            </span>
                          </button>;
                })}
                    </div>
                  </section>

                  {/* Display Options */}
                  <section data-id="element-2098">
                    <h4 className={`text-xs font-bold uppercase tracking-wider mb-3 ${isDark ? 'text-slate-400' : 'text-gray-500'}`} data-id="element-2099">
                      Display
                    </h4>
                    <div className="space-y-2" data-id="element-2100">
                      <ToggleRow label="Show Furniture" icon={Grid} checked={showFurniture} onChange={setShowFurniture} isDark={isDark} data-id="element-2101" />
                      <ToggleRow label="Show Doors" icon={Monitor} checked={showDoors} onChange={setShowDoors} isDark={isDark} data-id="element-2102" />
                      <ToggleRow label="Show Compass" icon={Compass} checked={showCompass} onChange={setShowCompass} isDark={isDark} data-id="element-2103" />
                      <ToggleRow label="Show Labels" icon={Type} checked={showLabels} onChange={setShowLabels} isDark={isDark} data-id="element-2104" />
                    </div>
                  </section>

                  {/* Graphics Quality */}
                  <section data-id="element-2105">
                    <div className="flex items-center justify-between mb-3" data-id="element-2106">
                      <h4 className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`} data-id="element-2107">
                        Graphics Quality
                      </h4>
                      <span className={`text-xs font-bold ${isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-2108">
                        {graphicsQuality}%
                      </span>
                    </div>
                    <input type="range" min="0" max="100" value={graphicsQuality} onChange={e => setGraphicsQuality(Number(e.target.value))} className="w-full h-1.5 bg-slate-200 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:h-3.5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-current text-teal-500 dark:text-cyan-500 dark:bg-slate-700" data-id="element-2109" />
                  </section>

                  {/* Theme */}
                  <section className={`pt-4 border-t ${isDark ? 'border-white/10' : 'border-gray-100'}`} data-id="element-2110">
                    <h4 className={`text-xs font-bold uppercase tracking-wider mb-3 ${isDark ? 'text-slate-400' : 'text-gray-500'}`} data-id="element-2111">
                      Theme
                    </h4>
                    <div className={`flex items-center justify-between p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-50'}`} data-id="element-2112">
                      <div className="flex items-center gap-3" data-id="element-2113">
                        <div className={`p-1.5 rounded-md ${isDark ? 'bg-cyan-500/20 text-cyan-400' : 'bg-teal-100 text-teal-600'}`} data-id="element-2114">
                          {isDark ? <Moon className="w-3.5 h-3.5" data-id="element-2115" /> : <Sun className="w-3.5 h-3.5" data-id="element-2116" />}
                        </div>
                        <span className={`text-xs font-medium ${isDark ? 'text-slate-200' : 'text-gray-700'}`} data-id="element-2117">
                          {isDark ? 'Dark Mode' : 'Light Mode'}
                        </span>
                      </div>
                      <button onClick={toggleTheme} className={`relative w-8 h-4 rounded-full transition-colors ${isDark ? 'bg-cyan-500' : 'bg-gray-300'}`} data-id="element-2118">
                        <motion.div animate={{
                    x: isDark ? 16 : 2
                  }} className="absolute top-0.5 left-0 w-3 h-3 bg-white rounded-full shadow-sm" data-id="element-2119" />
                      </button>
                    </div>
                  </section>

                  {/* Developer */}
                  <section className={`pt-4 border-t ${isDark ? 'border-white/10' : 'border-gray-100'}`} data-id="element-2120">
                    <h4 className={`text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-1.5 ${isDark ? 'text-amber-400/70' : 'text-amber-600'}`} data-id="element-2121">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500" data-id="element-2122" />
                      Developer
                    </h4>
                    <div className={`flex items-center justify-between p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-50'}`} data-id="element-2123">
                      <div className="flex flex-col gap-0.5" data-id="element-2124">
                        <span className={`text-xs font-medium ${isDark ? 'text-slate-200' : 'text-gray-700'}`} data-id="element-2125">
                          Allow Overrides
                        </span>
                        <span className={`text-[10px] ${isDark ? 'text-slate-500' : 'text-gray-400'}`} data-id="element-2126">
                          {allowOverrides ? 'Users can customize package items' : 'Package items are locked'}
                        </span>
                      </div>
                      <button onClick={() => setAllowOverrides(!allowOverrides)} className={`relative w-8 h-4 rounded-full transition-colors ${allowOverrides ? isDark ? 'bg-cyan-500' : 'bg-teal-500' : isDark ? 'bg-slate-700' : 'bg-gray-300'}`} data-id="element-2127">
                        <motion.div animate={{
                    x: allowOverrides ? 16 : 2
                  }} className="absolute top-0.5 left-0 w-3 h-3 bg-white rounded-full shadow-sm" data-id="element-2128" />
                      </button>
                    </div>
                  </section>
                </motion.div> : activeTab === 'controls' ? <motion.div key="controls" initial={{
            opacity: 0,
            x: 10
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -10
          }} transition={{
            duration: 0.15
          }} className="space-y-6" data-id="element-2129">
                  {/* Camera */}
                  <section data-id="element-2130">
                    <h4 className={`text-xs font-bold uppercase tracking-wider mb-4 ${isDark ? 'text-slate-400' : 'text-gray-500'}`} data-id="element-2131">
                      Camera
                    </h4>
                    <div className="space-y-4" data-id="element-2132">
                      <div data-id="element-2133">
                        <div className="flex justify-between text-xs mb-1.5" data-id="element-2134">
                          <span className={`flex items-center gap-1.5 ${isDark ? 'text-slate-300' : 'text-gray-600'}`} data-id="element-2135">
                            <ArrowUpDown className="w-3 h-3" data-id="element-2136" />
                            Height
                          </span>
                          <span className={isDark ? 'text-slate-500' : 'text-gray-400'} data-id="element-2137">
                            {cameraHeight}cm
                          </span>
                        </div>
                        <input type="range" min="100" max="190" value={cameraHeight} onChange={e => setCameraHeight(Number(e.target.value))} className={sliderClass} data-id="element-2138" />
                      </div>
                      <div data-id="element-2139">
                        <div className="flex justify-between text-xs mb-1.5" data-id="element-2140">
                          <span className={`flex items-center gap-1.5 ${isDark ? 'text-slate-300' : 'text-gray-600'}`} data-id="element-2141">
                            <ZoomIn className="w-3 h-3" data-id="element-2142" />
                            Zoom
                          </span>
                          <span className={isDark ? 'text-slate-500' : 'text-gray-400'} data-id="element-2143">
                            {zoomLevel}°
                          </span>
                        </div>
                        <input type="range" min="80" max="120" value={zoomLevel} onChange={e => setZoomLevel(Number(e.target.value))} className={sliderClass} data-id="element-2144" />
                      </div>
                    </div>
                  </section>

                  {/* Movement */}
                  <section data-id="element-2145">
                    <h4 className={`text-xs font-bold uppercase tracking-wider mb-4 ${isDark ? 'text-slate-400' : 'text-gray-500'}`} data-id="element-2146">
                      Movement
                    </h4>
                    <div className="space-y-4" data-id="element-2147">
                      <div data-id="element-2148">
                        <div className="flex justify-between text-xs mb-1.5" data-id="element-2149">
                          <span className={isDark ? 'text-slate-300' : 'text-gray-600'} data-id="element-2150">
                            Move Speed
                          </span>
                          <span className={isDark ? 'text-slate-500' : 'text-gray-400'} data-id="element-2151">
                            {moveSpeed}%
                          </span>
                        </div>
                        <input type="range" min="1" max="100" value={moveSpeed} onChange={e => setMoveSpeed(Number(e.target.value))} className={sliderClass} data-id="element-2152" />
                      </div>
                      <div data-id="element-2153">
                        <div className="flex justify-between text-xs mb-1.5" data-id="element-2154">
                          <span className={isDark ? 'text-slate-300' : 'text-gray-600'} data-id="element-2155">
                            Look Speed
                          </span>
                          <span className={isDark ? 'text-slate-500' : 'text-gray-400'} data-id="element-2156">
                            {lookSpeed}%
                          </span>
                        </div>
                        <input type="range" min="1" max="100" value={lookSpeed} onChange={e => setLookSpeed(Number(e.target.value))} className={sliderClass} data-id="element-2157" />
                      </div>
                    </div>
                  </section>

                  {/* Options */}
                  <section data-id="element-2158">
                    <h4 className={`text-xs font-bold uppercase tracking-wider mb-4 ${isDark ? 'text-slate-400' : 'text-gray-500'}`} data-id="element-2159">
                      Options
                    </h4>
                    <div className="space-y-4" data-id="element-2160">
                      <div className="flex items-center justify-between" data-id="element-2161">
                        <span className={`text-xs ${isDark ? 'text-slate-300' : 'text-gray-600'}`} data-id="element-2162">
                          Invert Vertical Axis
                        </span>
                        <button onClick={() => setInvertY(!invertY)} className={`relative w-8 h-4 rounded-full transition-colors ${invertY ? isDark ? 'bg-cyan-500' : 'bg-teal-500' : isDark ? 'bg-slate-700' : 'bg-gray-300'}`} data-id="element-2163">
                          <motion.div animate={{
                      x: invertY ? 16 : 2
                    }} className="absolute top-0.5 left-0 w-3 h-3 bg-white rounded-full shadow-sm" data-id="element-2164" />
                        </button>
                      </div>
                      <div className="flex items-center justify-between" data-id="element-2165">
                        <span className={`text-xs ${isDark ? 'text-slate-300' : 'text-gray-600'}`} data-id="element-2166">
                          Teleport to Option
                        </span>
                        <button onClick={() => setTeleportToOption(!teleportToOption)} className={`relative w-8 h-4 rounded-full transition-colors ${teleportToOption ? isDark ? 'bg-cyan-500' : 'bg-teal-500' : isDark ? 'bg-slate-700' : 'bg-gray-300'}`} data-id="element-2167">
                          <motion.div animate={{
                      x: teleportToOption ? 16 : 2
                    }} className="absolute top-0.5 left-0 w-3 h-3 bg-white rounded-full shadow-sm" data-id="element-2168" />
                        </button>
                      </div>
                    </div>
                  </section>

                  {/* How to Navigate — Collapsible */}
                  <NavigationGuide isDark={isDark} data-id="element-2169" />
                </motion.div> : null}
            </AnimatePresence>
          </div>
        </motion.div>}
    </AnimatePresence>;
}
function NavigationGuide({
  isDark
}: {
  isDark: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [showWasd, setShowWasd] = useState(true);
  useEffect(() => {
    if (!isOpen) return;
    const interval = setInterval(() => {
      setShowWasd(prev => !prev);
    }, 2500);
    return () => clearInterval(interval);
  }, [isOpen]);
  const cardClass = `rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-50'}`;
  return <section className={`pt-4 border-t ${isDark ? 'border-white/10' : 'border-gray-100'}`} data-id="element-2170">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between group" data-id="element-2171">
        <h4 className={`text-xs font-bold uppercase tracking-wider flex items-center gap-2 ${isDark ? 'text-slate-400' : 'text-gray-500'}`} data-id="element-2172">
          <Keyboard className="w-3 h-3" data-id="element-2173" />
          How to Navigate
        </h4>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.2
      }} data-id="element-2174">
          <ChevronDown className={`w-4 h-4 ${isDark ? 'text-slate-500' : 'text-gray-400'}`} data-id="element-2175" />
        </motion.div>
      </button>

      <AnimatePresence data-id="element-2176">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-2177">
            <div className="pt-3" data-id="element-2178">
              <NavigationGuideContent isDark={isDark} data-id="element-2179" />
            </div>
          </motion.div>}
      </AnimatePresence>
    </section>;
}
function NavigationGuideContent({
  isDark
}: {
  isDark: boolean;
}) {
  const [showWasd, setShowWasd] = useState(true);
  useEffect(() => {
    const interval = setInterval(() => {
      setShowWasd(prev => !prev);
    }, 2500);
    return () => clearInterval(interval);
  }, []);
  const cardClass = `rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-50'}`;
  return <div className="space-y-2" data-id="element-2180">
      {/* Row 1: Look/Rotate + Move */}
      <div className="grid grid-cols-2 gap-2" data-id="element-2181">
        {/* Look / Rotate */}
        <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0,
        duration: 0.15
      }} className={`${cardClass} p-3 flex items-center gap-2`} data-id="element-2182">
          <div className="flex flex-col gap-1.5 flex-shrink-0" data-id="element-2183">
            <span className={`text-[9px] font-bold uppercase tracking-wider leading-tight ${isDark ? 'text-slate-200' : 'text-gray-700'}`} data-id="element-2184">
              Look /<br data-id="element-2185" />
              Rotate
            </span>
            <kbd className={`text-[9px] font-mono px-2 py-0.5 rounded-md w-fit ${isDark ? 'bg-cyan-500/15 text-cyan-300 border border-cyan-500/30' : 'bg-teal-50 text-teal-700 border border-teal-200'}`} data-id="element-2186">
              Mouse
            </kbd>
          </div>
          <div className="flex-1 flex justify-end" data-id="element-2187">
            <svg viewBox="0 0 500 552" className="w-10 h-11" data-id="element-2188">
              <path fill={isDark ? '#269da4' : '#0d9488'} d="M120.98,224.55h129.02v-119.28s-96.32-15.9-129.02,119.28Z" data-id="element-2189" />
              <path fill={isDark ? '#29ffff' : '#14b8a6'} d="M250,92.63h0c-76.94,0-139.32,62.38-139.32,139.32v92.2c0,76.94,62.38,139.32,139.32,139.32h0c76.94,0,139.32-62.38,139.32-139.32v-92.2c0-76.94-62.38-139.32-139.32-139.32h0Z" data-id="element-2190" />
              <path fill={isDark ? '#269da4' : '#0d9488'} d="M250,113.63c31.6,0,61.32,12.31,83.67,34.66,22.35,22.35,34.66,52.06,34.66,83.67v92.2c0,31.6-12.31,61.32-34.66,83.67-22.35,22.35-52.06,34.66-83.67,34.66s-61.32-12.31-83.67-34.66c-22.35-22.35-34.66-52.06-34.66-83.67v-92.2c0-31.6,12.31-61.32,34.66-83.67,22.35-22.35,52.06-34.66,83.67-34.66Z" data-id="element-2191" />
              <line x1="250" y1="229.46" x2="250" y2="196.59" fill="none" stroke={isDark ? '#29ffff' : '#14b8a6'} strokeWidth="35" strokeLinecap="round" strokeMiterlimit="10" data-id="element-2192" />
              <polyline points="219.27 42.4 249.74 18.25 281.24 42.81" fill="none" stroke={isDark ? '#29ffff' : '#14b8a6'} strokeWidth="21" strokeLinecap="round" strokeMiterlimit="10" data-id="element-2193" />
              <polyline points="219.27 510.47 249.74 534.62 281.24 510.06" fill="none" stroke={isDark ? '#29ffff' : '#14b8a6'} strokeWidth="21" strokeLinecap="round" strokeMiterlimit="10" data-id="element-2194" />
              <polyline points="452.61 241.52 476.76 271.99 452.2 303.48" fill="none" stroke={isDark ? '#29ffff' : '#14b8a6'} strokeWidth="21" strokeLinecap="round" strokeMiterlimit="10" data-id="element-2195" />
              <polyline points="50.59 241.52 26.44 271.99 51 303.48" fill="none" stroke={isDark ? '#29ffff' : '#14b8a6'} strokeWidth="21" strokeLinecap="round" strokeMiterlimit="10" data-id="element-2196" />
            </svg>
          </div>
        </motion.div>

        {/* Move (cycling WASD / Arrows) */}
        <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.05,
        duration: 0.15
      }} className={`${cardClass} p-3 flex items-center gap-2`} data-id="element-2197">
          <div className="flex flex-col gap-1.5 flex-shrink-0" data-id="element-2198">
            <span className={`text-[9px] font-bold uppercase tracking-wider leading-tight ${isDark ? 'text-slate-200' : 'text-gray-700'}`} data-id="element-2199">
              Move
            </span>
            <kbd className={`text-[9px] font-mono px-2 py-0.5 rounded-md w-fit ${isDark ? 'bg-cyan-500/15 text-cyan-300 border border-cyan-500/30' : 'bg-teal-50 text-teal-700 border border-teal-200'}`} data-id="element-2200">
              WASD
            </kbd>
            <kbd className={`text-[9px] font-mono px-2 py-0.5 rounded-md w-fit ${isDark ? 'bg-cyan-500/15 text-cyan-300 border border-cyan-500/30' : 'bg-teal-50 text-teal-700 border border-teal-200'}`} data-id="element-2201">
              ← ↑ → ↓
            </kbd>
          </div>
          <div className="flex-1 flex justify-end relative h-11" data-id="element-2202">
            <AnimatePresence mode="wait" data-id="element-2203">
              {showWasd ? <motion.svg key="wasd" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.4
            }} viewBox="0 0 500 552" className="w-10 h-11 absolute right-0" data-id="element-2204">
                  <path fill={isDark ? '#269da4' : '#a3a3a3'} d="M137.11,280.93h-72.55c-18.24,0-33.03,14.79-33.03,33.03v72.55c0,18.24,14.79,33.03,33.03,33.03h72.55c18.24,0,33.03-14.79,33.03-33.03v-72.55c0-18.24-14.79-33.03-33.03-33.03h0Z" data-id="element-2205" />
                  <path fill={isDark ? '#269da4' : '#a3a3a3'} d="M291.92,280.93h-72.55c-18.24,0-33.03,14.79-33.03,33.03v72.55c0,18.24,14.79,33.03,33.03,33.03h72.55c18.24,0,33.03-14.79,33.03-33.03v-72.55c0-18.24-14.79-33.03-33.03-33.03h0Z" data-id="element-2206" />
                  <path fill={isDark ? '#269da4' : '#a3a3a3'} d="M446.73,280.93h-72.55c-18.24,0-33.03,14.79-33.03,33.03v72.55c0,18.24,14.79,33.03,33.03,33.03h72.55c18.24,0,33.03-14.79,33.03-33.03v-72.55c0-18.24-14.79-33.03-33.03-33.03h0Z" data-id="element-2207" />
                  <path fill={isDark ? '#29ffff' : '#14b8a6'} d="M291.92,125.47h-72.55c-18.24,0-33.03,14.79-33.03,33.03v72.55c0,18.24,14.79,33.03,33.03,33.03h72.55c18.24,0,33.03-14.79,33.03-33.03v-72.55c0-18.24-14.79-33.03-33.03-33.03ZM283.46,231.9h-15.16l-11.72-39.46h-.51l-11.72,39.46h-15.12l-18.94-65.07h17.38l9.82,42.64h.54l11.22-42.64h14.17l11.22,42.73h.54l9.85-42.73h17.35l-18.9,65.07Z" data-id="element-2208" />
                  <path fill={isDark ? '#29ffff' : '#14b8a6'} d="M86.27,383.81h-16.9l21.96-65.07h20.94l21.95,65.07h-16.9l-15.28-48.67h-.51l-15.25,48.67ZM84.02,358.2h35.33v11.95h-35.33v-11.95Z" data-id="element-2209" />
                  <path fill={isDark ? '#29ffff' : '#14b8a6'} d="M265.19,338.25c-.21-2.33-1.15-4.15-2.81-5.45-1.66-1.3-4.03-1.95-7.1-1.95-2.03,0-3.73.27-5.08.79-1.36.53-2.37,1.25-3.05,2.18-.68.92-1.03,1.98-1.05,3.16-.04.97.15,1.83.59,2.57.43.74,1.07,1.39,1.91,1.95.84.56,1.85,1.05,3.03,1.48,1.19.42,2.52.79,4,1.11l5.59,1.27c3.22.7,6.06,1.63,8.53,2.8,2.47,1.17,4.54,2.55,6.23,4.16,1.68,1.61,2.96,3.46,3.83,5.56.87,2.1,1.31,4.45,1.33,7.05-.02,4.11-1.06,7.64-3.11,10.58s-5,5.2-8.85,6.77c-3.84,1.57-8.48,2.35-13.9,2.35s-10.19-.82-14.23-2.45c-4.05-1.63-7.19-4.11-9.44-7.43-2.25-3.33-3.4-7.53-3.46-12.61h15.06c.13,2.1.69,3.84,1.68,5.24,1,1.4,2.37,2.46,4.12,3.18,1.75.72,3.78,1.08,6.08,1.08,2.12,0,3.92-.29,5.42-.86s2.64-1.37,3.45-2.38,1.22-2.18,1.24-3.5c-.02-1.23-.4-2.28-1.14-3.16-.74-.88-1.88-1.64-3.42-2.29-1.54-.65-3.49-1.24-5.86-1.79l-6.8-1.59c-5.63-1.29-10.07-3.38-13.31-6.27-3.24-2.89-4.85-6.8-4.83-11.74-.02-4.02,1.06-7.55,3.24-10.58s5.2-5.39,9.06-7.09c3.85-1.69,8.25-2.54,13.19-2.54s9.42.85,13.14,2.56,6.6,4.09,8.66,7.16c2.05,3.07,3.1,6.63,3.15,10.68h-15.06Z" data-id="element-2210" />
                  <path fill={isDark ? '#29ffff' : '#14b8a6'} d="M408.76,383.81h-24.05v-65.07h24.02c6.63,0,12.34,1.3,17.14,3.89s8.5,6.32,11.1,11.18c2.61,4.86,3.91,10.67,3.91,17.43s-1.3,12.6-3.89,17.47c-2.59,4.87-6.3,8.61-11.1,11.2s-10.52,3.89-17.12,3.89ZM400.44,370.4h7.72c3.64,0,6.73-.62,9.26-1.86,2.53-1.24,4.45-3.26,5.75-6.07,1.3-2.81,1.95-6.55,1.95-11.23s-.66-8.41-1.97-11.2c-1.31-2.79-3.25-4.8-5.8-6.04-2.55-1.24-5.69-1.86-9.42-1.86h-7.5v38.25Z" data-id="element-2211" />
                </motion.svg> : <motion.svg key="arrows" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.4
            }} viewBox="0 0 500 552" className="w-10 h-11 absolute right-0" data-id="element-2212">
                  <path fill={isDark ? '#269da4' : '#a3a3a3'} d="M137.11,280.93h-72.55c-18.24,0-33.03,14.79-33.03,33.03v72.55c0,18.24,14.79,33.03,33.03,33.03h72.55c18.24,0,33.03-14.79,33.03-33.03v-72.55c0-18.24-14.79-33.03-33.03-33.03h0Z" data-id="element-2213" />
                  <path fill={isDark ? '#269da4' : '#a3a3a3'} d="M291.92,280.93h-72.55c-18.24,0-33.03,14.79-33.03,33.03v72.55c0,18.24,14.79,33.03,33.03,33.03h72.55c18.24,0,33.03-14.79,33.03-33.03v-72.55c0-18.24-14.79-33.03-33.03-33.03h0Z" data-id="element-2214" />
                  <path fill={isDark ? '#269da4' : '#a3a3a3'} d="M446.73,280.93h-72.55c-18.24,0-33.03,14.79-33.03,33.03v72.55c0,18.24,14.79,33.03,33.03,33.03h72.55c18.24,0,33.03-14.79,33.03-33.03v-72.55c0-18.24-14.79-33.03-33.03-33.03h0Z" data-id="element-2215" />
                  <path fill={isDark ? '#29ffff' : '#14b8a6'} d="M291.92,125.47h-72.55c-18.24,0-33.03,14.79-33.03,33.03v72.55c0,18.24,14.79,33.03,33.03,33.03h72.55c18.24,0,33.03-14.79,33.03-33.03v-72.55c0-18.24-14.79-33.03-33.03-33.03ZM295.41,213.51c-2.07,2.65-5.16,4.04-8.29,4.04-2.26,0-4.53-.72-6.45-2.22l-24.99-19.49-24,19.02c-4.54,3.6-11.15,2.84-14.75-1.71-3.6-4.54-2.84-11.15,1.71-14.75l36.94-29.28,38,29.64c4.57,3.57,5.39,10.16,1.82,14.74Z" data-id="element-2216" />
                  <polyline points="228.72 343.04 255.7 367.19 283.59 342.62" fill="none" stroke={isDark ? '#29ffff' : '#14b8a6'} strokeWidth="21" strokeLinecap="round" strokeMiterlimit="10" data-id="element-2217" />
                  <polyline points="110.39 323.92 86.24 350.89 110.8 378.77" fill="none" stroke={isDark ? '#29ffff' : '#14b8a6'} strokeWidth="21" strokeLinecap="round" strokeMiterlimit="10" data-id="element-2218" />
                  <polyline points="400.89 323.92 425.04 350.89 400.48 378.77" fill="none" stroke={isDark ? '#29ffff' : '#14b8a6'} strokeWidth="21" strokeLinecap="round" strokeMiterlimit="10" data-id="element-2219" />
                </motion.svg>}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>

      {/* Row 2: Zoom + Move to Point */}
      <div className="grid grid-cols-2 gap-2" data-id="element-2220">
        {/* Zoom In / Out */}
        <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1,
        duration: 0.15
      }} className={`${cardClass} p-3 flex items-center gap-2`} data-id="element-2221">
          <div className="flex flex-col gap-1.5 flex-shrink-0" data-id="element-2222">
            <span className={`text-[9px] font-bold uppercase tracking-wider leading-tight ${isDark ? 'text-slate-200' : 'text-gray-700'}`} data-id="element-2223">
              Zoom
              <br data-id="element-2224" />
              In / Out
            </span>
            <kbd className={`text-[9px] font-mono px-2 py-0.5 rounded-md w-fit ${isDark ? 'bg-cyan-500/15 text-cyan-300 border border-cyan-500/30' : 'bg-teal-50 text-teal-700 border border-teal-200'}`} data-id="element-2225">
              Scroll
            </kbd>
          </div>
          <div className="flex-1 flex justify-end" data-id="element-2226">
            <svg viewBox="0 0 500 552" className="w-10 h-11" data-id="element-2227">
              <path fill={isDark ? '#269da4' : '#a3a3a3'} d="M250,113.63c31.6,0,61.32,12.31,83.67,34.66,22.35,22.35,34.66,52.06,34.66,83.67v92.2c0,31.6-12.31,61.32-34.66,83.67-22.35,22.35-52.06,34.66-83.67,34.66s-61.32-12.31-83.67-34.66c-22.35-22.35-34.66-52.06-34.66-83.67v-92.2c0-31.6,12.31-61.32,34.66-83.67,22.35-22.35,52.06-34.66,83.67-34.66Z" data-id="element-2228" />
              <path fill={isDark ? '#1e293b' : '#e5e7eb'} d="M250,92.63h0c-76.94,0-139.32,62.38-139.32,139.32v92.2c0,76.94,62.38,139.32,139.32,139.32h0c76.94,0,139.32-62.38,139.32-139.32v-92.2c0-76.94-62.38-139.32-139.32-139.32h0Z" strokeWidth="0" data-id="element-2229" />
              <path fill={isDark ? '#269da4' : '#a3a3a3'} d="M250,113.63c31.6,0,61.32,12.31,83.67,34.66,22.35,22.35,34.66,52.06,34.66,83.67v92.2c0,31.6-12.31,61.32-34.66,83.67-22.35,22.35-52.06,34.66-83.67,34.66s-61.32-12.31-83.67-34.66c-22.35-22.35-34.66-52.06-34.66-83.67v-92.2c0-31.6,12.31-61.32,34.66-83.67,22.35-22.35,52.06-34.66,83.67-34.66Z" data-id="element-2230" />
              <line x1="250" y1="229.46" x2="250" y2="196.59" fill="none" stroke={isDark ? '#29ffff' : '#14b8a6'} strokeWidth="35" strokeLinecap="round" strokeMiterlimit="10" data-id="element-2231" />
              <polyline points="219.27 68.09 249.74 43.94 281.24 68.5" fill="none" stroke={isDark ? '#29ffff' : '#14b8a6'} strokeWidth="21" strokeLinecap="round" strokeMiterlimit="10" data-id="element-2232" />
              <line x1="250.26" y1="56.22" x2="250.26" y2="135.65" fill="none" stroke={isDark ? '#29ffff' : '#14b8a6'} strokeWidth="21" strokeLinecap="round" strokeMiterlimit="10" data-id="element-2233" />
              <polyline points="219.27 353.84 249.74 377.99 281.24 353.43" fill="none" stroke={isDark ? '#29ffff' : '#14b8a6'} strokeWidth="21" strokeLinecap="round" strokeMiterlimit="10" data-id="element-2234" />
              <line x1="250.26" y1="365.71" x2="250.26" y2="286.28" fill="none" stroke={isDark ? '#29ffff' : '#14b8a6'} strokeWidth="21" strokeLinecap="round" strokeMiterlimit="10" data-id="element-2235" />
            </svg>
          </div>
        </motion.div>

        {/* Move to Point */}
        <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.15,
        duration: 0.15
      }} className={`${cardClass} p-3 flex items-center gap-2`} data-id="element-2236">
          <div className="flex flex-col gap-1.5 flex-shrink-0" data-id="element-2237">
            <span className={`text-[9px] font-bold uppercase tracking-wider leading-tight ${isDark ? 'text-slate-200' : 'text-gray-700'}`} data-id="element-2238">
              Move
              <br data-id="element-2239" />
              to Point
            </span>
            <kbd className={`text-[9px] font-mono px-2 py-0.5 rounded-md w-fit ${isDark ? 'bg-cyan-500/15 text-cyan-300 border border-cyan-500/30' : 'bg-teal-50 text-teal-700 border border-teal-200'}`} data-id="element-2240">
              Click
            </kbd>
          </div>
          <div className="flex-1 flex justify-end" data-id="element-2241">
            <svg viewBox="0 0 500 552" className="w-10 h-11" data-id="element-2242">
              <path fill={isDark ? '#29ffff' : '#14b8a6'} d="M128.82,20.64c-44.3,0-80.2,35.91-80.2,80.2s35.91,80.2,80.2,80.2,80.2-35.91,80.2-80.2S173.12,20.64,128.82,20.64ZM103.85,112.26l-6.06-10.95-5.9,10.95h-10.65l9.58-16.49-9.11-16.49h10.65l5.43,10.87,5.63-10.87h10.59l-9.21,16.49,9.56,16.49h-10.5ZM164.3,121.43h-42.22v-9.92l20.8-18.22c1.56-1.41,2.89-2.71,3.98-3.89,1.1-1.18,1.93-2.37,2.51-3.57.57-1.2.86-2.5.86-3.91,0-1.58-.34-2.93-1.03-4.05-.69-1.12-1.63-1.99-2.84-2.6-1.21-.61-2.59-.92-4.15-.92s-2.96.32-4.15.96c-1.19.64-2.11,1.56-2.77,2.77-.66,1.21-.99,2.67-.99,4.4h-13.06c0-3.88.87-7.23,2.62-10.05,1.75-2.82,4.2-5,7.37-6.53,3.16-1.53,6.85-2.3,11.04-2.3s8.09.72,11.28,2.17c3.19,1.45,5.67,3.47,7.42,6.06,1.75,2.59,2.63,5.59,2.63,9.01,0,2.17-.44,4.33-1.31,6.46-.87,2.14-2.44,4.49-4.69,7.07-2.26,2.58-5.45,5.67-9.6,9.28l-6.82,6.29v.36h23.12v11.11Z" data-id="element-2243" />
              <path fill={isDark ? '#269da4' : '#a3a3a3'} d="M120.98,224.55h129.02v-119.28s-96.32-15.9-129.02,119.28Z" data-id="element-2244" />
              <path fill={isDark ? '#1e293b' : '#e5e7eb'} d="M250,92.63h0c-76.94,0-139.32,62.38-139.32,139.32v92.2c0,76.94,62.38,139.32,139.32,139.32h0c76.94,0,139.32-62.38,139.32-139.32v-92.2c0-76.94-62.38-139.32-139.32-139.32h0Z" strokeWidth="0" data-id="element-2245" />
              <path fill={isDark ? '#29ffff' : '#14b8a6'} d="M250,113.63c31.6,0,61.32,12.31,83.67,34.66s34.66,52.06,34.66,83.67v92.2c0,31.6-12.31,61.32-34.66,83.67-22.35,22.35-52.06,34.66-83.67,34.66s-61.32-12.31-83.67-34.66-34.66-52.06-34.66-83.67v-92.2c0-31.6,12.31-61.32,34.66-83.67,22.35-22.35,52.06-34.66,83.67-34.66Z" data-id="element-2246" />
              <line x1="250" y1="229.46" x2="250" y2="196.59" fill="none" stroke={isDark ? '#29ffff' : '#14b8a6'} strokeWidth="35" strokeLinecap="round" strokeMiterlimit="10" data-id="element-2247" />
            </svg>
          </div>
        </motion.div>
      </div>

      {/* Row 3: Right Click (full width) */}
      <motion.div initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2,
      duration: 0.15
    }} className={`${cardClass} p-3 flex items-center gap-3`} data-id="element-2248">
        <div className="flex-1 flex flex-col gap-1" data-id="element-2249">
          <span className={`text-[9px] font-bold uppercase tracking-wider ${isDark ? 'text-slate-200' : 'text-gray-700'}`} data-id="element-2250">
            Right Click
          </span>
          <span className={`text-[9px] leading-tight ${isDark ? 'text-slate-400' : 'text-gray-500'}`} data-id="element-2251">
            Collapse menus / Free navigation
          </span>
        </div>
        <div className="flex-shrink-0" data-id="element-2252">
          <svg viewBox="0 0 500 552" className="w-10 h-11" data-id="element-2253">
            <path fill={isDark ? '#269da4' : '#a3a3a3'} d="M379.02,224.55h-129.02v-119.28s96.32-15.9,129.02,119.28Z" data-id="element-2254" />
            <path fill={isDark ? '#1e293b' : '#e5e7eb'} d="M250,92.63h0c-76.94,0-139.32,62.38-139.32,139.32v92.2c0,76.94,62.38,139.32,139.32,139.32h0c76.94,0,139.32-62.38,139.32-139.32v-92.2c0-76.94-62.38-139.32-139.32-139.32h0Z" strokeWidth="0" data-id="element-2255" />
            <path fill={isDark ? '#29ffff' : '#14b8a6'} d="M250,113.63c31.6,0,61.32,12.31,83.67,34.66s34.66,52.06,34.66,83.67v92.2c0,31.6-12.31,61.32-34.66,83.67-22.35,22.35-52.06,34.66-83.67,34.66s-61.32-12.31-83.67-34.66-34.66-52.06-34.66-83.67v-92.2c0-31.6,12.31-61.32,34.66-83.67,22.35-22.35,52.06-34.66,83.67-34.66Z" data-id="element-2256" />
            <line x1="250" y1="229.46" x2="250" y2="196.59" fill="none" stroke={isDark ? '#29ffff' : '#14b8a6'} strokeWidth="35" strokeLinecap="round" strokeMiterlimit="10" data-id="element-2257" />
            <line x1="331.3" y1="80.65" x2="341.92" y2="50.38" fill="none" stroke={isDark ? '#29ffff' : '#14b8a6'} strokeWidth="21" strokeLinecap="round" strokeMiterlimit="10" data-id="element-2258" />
            <line x1="371.08" y1="101.04" x2="395.62" y2="75.19" fill="none" stroke={isDark ? '#29ffff' : '#14b8a6'} strokeWidth="21" strokeLinecap="round" strokeMiterlimit="10" data-id="element-2259" />
            <line x1="395.62" y1="133.75" x2="425.23" y2="121.41" fill="none" stroke={isDark ? '#29ffff' : '#14b8a6'} strokeWidth="21" strokeLinecap="round" strokeMiterlimit="10" data-id="element-2260" />
          </svg>
        </div>
      </motion.div>
    </div>;
}
function ToggleRow({
  label,
  icon: Icon,
  checked,
  onChange,
  isDark
}: {
  label: string;
  icon: any;
  checked: boolean;
  onChange: (val: boolean) => void;
  isDark: boolean;
}) {
  return <button onClick={() => onChange(!checked)} className={`w-full flex items-center justify-between p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`} data-id="element-2261">
      <div className="flex items-center gap-3" data-id="element-2262">
        <div className={`p-1.5 rounded-md ${checked ? isDark ? 'bg-cyan-500/20 text-cyan-400' : 'bg-teal-100 text-teal-600' : isDark ? 'bg-slate-800 text-slate-500' : 'bg-gray-100 text-gray-400'}`} data-id="element-2263">
          <Icon className="w-3.5 h-3.5" data-id="element-2264" />
        </div>
        <span className={`text-xs font-medium ${isDark ? 'text-slate-200' : 'text-gray-700'}`} data-id="element-2265">
          {label}
        </span>
      </div>
      <div className="relative" data-id="element-2266">
        {checked ? <Eye className={`w-4 h-4 ${isDark ? 'text-cyan-400' : 'text-teal-500'}`} data-id="element-2267" /> : <EyeOff className={`w-4 h-4 ${isDark ? 'text-slate-600' : 'text-gray-400'}`} data-id="element-2268" />}
      </div>
    </button>;
}